// The naming sheet of a seamount (v2-the-deep.md §4.3, round 088 card 06,
// R2606, R2620, R2626).
//
// The commander whose hull found a seamount names it. The Empire frame
// lists the finds of the commander that carry no name yet
// (`Empire.seamount_finds`), and the overlay offers each one the first time
// it sees it, in the rename card of the context slot (R1818). The sheet
// holds two pickers of the word list of `[ui.seamount_name]`, and, while
// the seat holds the key `free_text`, a box for a free-text name of
// `SEAMOUNT_NAME_MIN_RUNES` to `SEAMOUNT_NAME_MAX_RUNES` runes. NAME sends
// one `NameSeamount`; LATER puts the sheet away. The server is the judge
// of the name, the key and the finder, and a refusal arrives as an error
// notice (`seamount_*` of `[notices.hub]`).
//
// Everything here is pure, so a test reads the sheet with no overlay.

import type { NameSeamount, NamedSeamount } from '../gen/proto/aquaphobia';
import { SEAMOUNT_NAME_MAX_RUNES, SEAMOUNT_NAME_MIN_RUNES } from '../gen/balance';
import { card as S, seamountName as W } from '../gen/strings';
import { runeCount } from '../util/text';
import { esc } from './esc';
import { selectBox, type Choice } from './choice/choice';
import type { CardSpec } from './cards/card';
import { markSvg } from './cards/marks';

/** The act of the first picker, the second picker and NAME. */
export const SM_FIRST = 'smfirst';
export const SM_SECOND = 'smsecond';
export const SM_SAVE = 'smsave';
/** LATER is the act of the rename card's CANCEL: one card, one close. */
const SM_LATER = 'renamecancel';

/** What the sheet holds while it stands. */
export interface NamingDraft {
  find: NamedSeamount;
  /** The place of each picked word in its list. */
  first: number;
  second: number;
  /** The seat holds the key `free_text`. */
  freeText: boolean;
}

/** The word-list name of two places, in the pattern of the reader's
 *  language, or '' for a place off a list. */
export function wordName(first: number, second: number): string {
  const a = W.first[first], b = W.second[second];
  if (a === undefined || b === undefined) return '';
  return W.pattern({ first: a, second: b });
}

/** Does a free text fit the length the server holds a name to? */
export function textFits(text: string): boolean {
  const n = runeCount(text.trim());
  return n >= SEAMOUNT_NAME_MIN_RUNES && n <= SEAMOUNT_NAME_MAX_RUNES;
}

/**
 * The first place of each list for a seamount: the body id spread over the
 * lists, so two finds of one commander open on two names. The pick is the
 * commander's; this is only the place the pickers start at.
 */
export function startWords(body: number): { first: number; second: number } {
  return { first: body % W.first.length, second: (body * 7) % W.second.length };
}

/**
 * The intent of NAME: the free text while the seat holds the key and the
 * box holds text, else the two places. A free text the server would refuse
 * on its length answers null, so the sheet states the rule and sends
 * nothing.
 */
export function seamountIntent(d: NamingDraft, text: string): NameSeamount | null {
  const t = text.trim();
  if (d.freeText && t !== '') {
    if (!textFits(t)) return null;
    return { body: d.find.body, text: t, first: 0, second: 0 };
  }
  return { body: d.find.body, text: '', first: d.first, second: d.second };
}

/** The finds the overlay has not offered yet, in body order. */
export function freshFinds(finds: readonly NamedSeamount[], offered: ReadonlySet<number>): NamedSeamount[] {
  return finds.filter((f) => !offered.has(f.body));
}

/**
 * The NAME press of the pinned peek over the rock of an own find that has
 * no name yet (R2633), or null for any other ridge. It opens the naming
 * sheet of that find, so LATER does not put a find away until a reload.
 */
export function namePress(
  r: { find?: NamedSeamount } | null, open: (body: number) => void,
): { label: string; run: () => void } | null {
  const find = r?.find;
  if (!find) return null;
  return { label: S.seamount_name_btn, run: () => open(find.body) };
}

/** The choices of one word list: the place as the id. */
function choices(list: readonly string[]): Choice[] {
  return list.map((w, i) => ({ id: String(i), label: w }));
}

/**
 * The naming card (round 088 card 06): the seamount mark, the two
 * pickers, the name they make, and the free-text box while the seat holds
 * the key. The box is 16 px, so iOS does not zoom (R1818).
 */
export function namingCard(d: NamingDraft): CardSpec {
  const pickers = '<div class="ck-sect ck-smw">'
    + selectBox(choices(W.first), String(d.first), SM_FIRST, { label: S.seamount_first, uid: 'smfirst', size: 'compact' })
    + selectBox(choices(W.second), String(d.second), SM_SECOND, { label: S.seamount_second, uid: 'smsecond', size: 'compact' })
    + `</div><p class="ck-smn">${esc(S.seamount_preview({ name: wordName(d.first, d.second) }))}</p>`;
  const box = d.freeText
    ? '<div class="ck-sect">'
      + `<input class="renin" type="text" autocomplete="off" maxlength="${SEAMOUNT_NAME_MAX_RUNES * 2}" `
      + `aria-label="${esc(S.seamount_text)}" placeholder="${esc(S.seamount_text)}"></div>`
    : '';
  const lines = [esc(S.seamount_hint_words)];
  if (d.freeText) lines.push(esc(S.seamount_hint_text({ min: SEAMOUNT_NAME_MIN_RUNES, max: SEAMOUNT_NAME_MAX_RUNES })));
  return {
    size: 'card', mark: markSvg('ridge', 'var(--text)'), title: S.seamount_title, ink: 'neutral',
    kind: S.seamount_kind, close: false,
    body: pickers + box,
    note: { lines },
    actions: [
      { act: SM_SAVE, label: S.seamount_name_btn, face: 'dark' },
      { act: SM_LATER, label: S.seamount_later_btn, face: 'quiet' },
    ],
  };
}
